/**
 * Data Migration Utility
 * Migrates from single-document structure to multi-document structure
 * 
 * OLD: groups/{groupId}/data/main (everything in one doc)
 * NEW: 
 *   - groups/{groupId}/data/main (shows, contacts, expenses, settings)
 *   - groups/{groupId}/inventory/{itemId} (each inventory item separate)
 *   - groups/{groupId}/documents/{docId} (each document separate)
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  getDocs, 
  writeBatch,
  deleteField,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Check if migration is needed
 */
export const checkMigrationNeeded = async (groupId) => {
  try {
    const mainDoc = await getDoc(doc(db, 'groups', groupId, 'data', 'main'));
    if (!mainDoc.exists()) return { needed: false, reason: 'No data document' };
    
    const data = mainDoc.data();
    
    // Check if inventory is still in main doc as array
    if (Array.isArray(data.inventory) && data.inventory.length > 0) {
      return { 
        needed: true, 
        reason: 'Inventory in main document',
        inventoryCount: data.inventory.length,
        estimatedSize: JSON.stringify(data).length
      };
    }
    
    return { needed: false, reason: 'Already migrated or no inventory' };
  } catch (error) {
    console.error('Error checking migration:', error);
    return { needed: false, reason: error.message };
  }
};

/**
 * Remove duplicate items from inventory array
 */
export const removeDuplicates = (inventory) => {
  const seen = new Map();
  const unique = [];
  
  for (const item of inventory) {
    // Create a key based on item properties (excluding id and timestamps)
    const key = `${item.item}-${item.size}-${item.sku || ''}-${item.sellPrice}`;
    
    if (!seen.has(key)) {
      seen.set(key, true);
      unique.push(item);
    } else {
      console.log('Removing duplicate:', item.item, item.size);
    }
  }
  
  return unique;
};

/**
 * Clean up duplicates in current structure (quick fix)
 */
export const cleanupDuplicates = async (groupId) => {
  try {
    const mainRef = doc(db, 'groups', groupId, 'data', 'main');
    const mainDoc = await getDoc(mainRef);
    
    if (!mainDoc.exists()) {
      return { success: false, error: 'No data document found' };
    }
    
    const data = mainDoc.data();
    
    if (!Array.isArray(data.inventory)) {
      return { success: false, error: 'No inventory array found' };
    }
    
    const originalCount = data.inventory.length;
    const cleanedInventory = removeDuplicates(data.inventory);
    const removedCount = originalCount - cleanedInventory.length;
    
    if (removedCount === 0) {
      return { success: true, message: 'No duplicates found', removedCount: 0 };
    }
    
    // Update with cleaned inventory
    await updateDoc(mainRef, {
      inventory: cleanedInventory,
      lastUpdated: new Date().toISOString()
    });
    
    return { 
      success: true, 
      message: `Removed ${removedCount} duplicates`,
      originalCount,
      newCount: cleanedInventory.length,
      removedCount
    };
  } catch (error) {
    console.error('Error cleaning duplicates:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Migrate inventory to separate collection
 */
export const migrateInventoryToCollection = async (groupId, onProgress) => {
  try {
    const mainRef = doc(db, 'groups', groupId, 'data', 'main');
    const mainDoc = await getDoc(mainRef);
    
    if (!mainDoc.exists()) {
      return { success: false, error: 'No data document found' };
    }
    
    const data = mainDoc.data();
    
    if (!Array.isArray(data.inventory) || data.inventory.length === 0) {
      return { success: true, message: 'No inventory to migrate' };
    }
    
    // Remove duplicates first
    const inventory = removeDuplicates(data.inventory);
    const total = inventory.length;
    
    onProgress?.({ phase: 'starting', total });
    
    // Batch write inventory items to subcollection
    const inventoryRef = collection(db, 'groups', groupId, 'inventory');
    let batch = writeBatch(db);
    let batchCount = 0;
    let migrated = 0;
    
    for (const item of inventory) {
      const itemRef = doc(inventoryRef, item.id || `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
      batch.set(itemRef, {
        ...item,
        migratedAt: new Date().toISOString()
      });
      
      batchCount++;
      migrated++;
      
      // Firestore batch limit is 500
      if (batchCount >= 450) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
        onProgress?.({ phase: 'migrating', migrated, total });
      }
    }
    
    // Commit remaining
    if (batchCount > 0) {
      await batch.commit();
    }
    
    onProgress?.({ phase: 'cleaning', migrated, total });
    
    // Remove inventory from main document
    await updateDoc(mainRef, {
      inventory: deleteField(),
      inventoryMigrated: true,
      inventoryMigratedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    });
    
    onProgress?.({ phase: 'complete', migrated, total });
    
    return { 
      success: true, 
      message: `Migrated ${migrated} inventory items`,
      migratedCount: migrated
    };
  } catch (error) {
    console.error('Error migrating inventory:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Load inventory from new collection structure
 */
export const loadInventoryFromCollection = async (groupId) => {
  try {
    const inventoryRef = collection(db, 'groups', groupId, 'inventory');
    const snapshot = await getDocs(inventoryRef);
    
    const inventory = [];
    snapshot.forEach(doc => {
      inventory.push({ id: doc.id, ...doc.data() });
    });
    
    return inventory;
  } catch (error) {
    console.error('Error loading inventory:', error);
    return [];
  }
};

/**
 * Check current document size
 */
export const getDocumentSize = async (groupId) => {
  try {
    const mainDoc = await getDoc(doc(db, 'groups', groupId, 'data', 'main'));
    if (!mainDoc.exists()) return { size: 0, formatted: '0 KB' };
    
    const data = mainDoc.data();
    const size = JSON.stringify(data).length;
    
    return {
      size,
      formatted: size > 1024 * 1024 
        ? `${(size / (1024 * 1024)).toFixed(2)} MB`
        : `${(size / 1024).toFixed(2)} KB`,
      percentOfLimit: ((size / (1024 * 1024)) * 100).toFixed(1),
      isOverLimit: size > 1024 * 1024,
      breakdown: {
        shows: JSON.stringify(data.shows || {}).length,
        inventory: JSON.stringify(data.inventory || []).length,
        expenses: JSON.stringify(data.expenses || data.generalExpenses || []).length,
        contacts: JSON.stringify(data.contacts || []).length,
        documents: JSON.stringify(data.documents || []).length
      }
    };
  } catch (error) {
    console.error('Error getting document size:', error);
    return { size: 0, error: error.message };
  }
};

export default {
  checkMigrationNeeded,
  cleanupDuplicates,
  migrateInventoryToCollection,
  loadInventoryFromCollection,
  getDocumentSize,
  removeDuplicates
};